import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Slider,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  VolumeUp,
  Block,
  DirectionsBike,
  Bedtime,
  Star,
  Language,
  Link as LinkIcon,
  Add,
  Remove,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useDevice } from '../../contexts/DeviceContext';
import { TabletNowSlot } from '../TabletNowSlot';

const ControlsPage: React.FC = () => {
  const { t } = useTranslation();
  const {
    selectedDevice,
    deviceActions,
    reloadActions,
    handleVolumeChange,
    handleBanCurrentUrl,
    handleFavoriteCurrentUrl,
    handleSendToBike,
    handleSendToSleep,
  } = useDevice();

  const [openDialog, setOpenDialog] = useState('');
  const [bikeMinutes, setBikeMinutes] = useState(30);
  const [sleepMinutes, setSleepMinutes] = useState(60);

  // Extract domain from URL
  const getDomainFromUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname;
    } catch {
      return url;
    }
  };

  if (!selectedDevice) return null;

  const handleBikeSubmit = async () => {
    await handleSendToBike(selectedDevice.id, bikeMinutes);
    setOpenDialog('');
  };

  const handleSleepSubmit = async () => {
    await handleSendToSleep(selectedDevice.id, sleepMinutes);
    setOpenDialog('');
  };

  return (
    <>
      {/* What is on the tablet now: a sent schedule's live panel or the current action,
          and the last sent schedule's report (BA-85). Keyed, so nothing carries over to
          another device */}
      <Box sx={{ mb: 3 }}>
        <TabletNowSlot
          key={selectedDevice.id}
          deviceId={selectedDevice.id}
          deviceName={selectedDevice.name}
          canSend={selectedDevice.role !== 'viewer'}
          actions={deviceActions}
          onReloadActions={reloadActions}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' }, flexWrap: 'wrap' }}>
        {/* Volume Control */}
        <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' } }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                <VolumeUp sx={{ mr: 1, verticalAlign: 'middle' }} />
                {t('volume.title')}
              </Typography>
              <Box sx={{ px: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton
                  size="large"
                  color="primary"
                  onClick={() => handleVolumeChange(selectedDevice.id, Math.max(1, (selectedDevice.volume || 5) - 1))}
                  disabled={(selectedDevice.volume || 5) <= 1}
                  sx={{ border: 1, borderColor: 'divider' }}
                >
                  <Remove fontSize="large" />
                </IconButton>
                <Slider
                  value={selectedDevice.volume || 5}
                  onChange={(e, v) => handleVolumeChange(selectedDevice.id, v as number)}
                  min={1}
                  max={10}
                  marks
                  valueLabelDisplay="auto"
                  sx={{ mx: 1 }}
                />
                <IconButton
                  size="large"
                  color="primary"
                  onClick={() => handleVolumeChange(selectedDevice.id, Math.min(10, (selectedDevice.volume || 5) + 1))}
                  disabled={(selectedDevice.volume || 5) >= 10}
                  sx={{ border: 1, borderColor: 'divider' }}
                >
                  <Add fontSize="large" />
                </IconButton>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Browser Control */}
        <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(50% - 12px)' } }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('browser.title')}
              </Typography>
              {selectedDevice.current_url && (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                    {t('browser.currentUrl')}
                  </Typography>
                  <Link
                    href={selectedDevice.current_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ wordBreak: 'break-all' }}
                  >
                    {selectedDevice.current_url}
                  </Link>
                </Box>
              )}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<Block />}
                  onClick={() => {
                    if (selectedDevice.current_url) {
                      setOpenDialog('ban');
                    } else {
                      handleBanCurrentUrl(selectedDevice.id);
                    }
                  }}
                >
                  {selectedDevice.current_url ? t('browser.banCurrentUrl') : t('browser.setTestUrl')}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Star />}
                  onClick={() => handleFavoriteCurrentUrl(selectedDevice.id)}
                >
                  {selectedDevice.current_url ? t('browser.favoriteCurrentUrl') : t('browser.setTestUrl')}
                </Button>
                <Button
                  variant="contained"
                  color="info"
                  startIcon={<DirectionsBike />}
                  onClick={() => setOpenDialog('bike')}
                >
                  {t('bike.sendToBike')}
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<Bedtime />}
                  onClick={() => setOpenDialog('sleep')}
                >
                  {t('sleep.sendToSleep')}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Bike Dialog */}
      <Dialog open={openDialog === 'bike'} onClose={() => setOpenDialog('')}>
        <DialogTitle>{t('bike.dialogTitle')}</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            {t('bike.dialogText')}
          </Typography>
          <Slider
            value={bikeMinutes}
            onChange={(e, v) => setBikeMinutes(v as number)}
            min={5}
            max={120}
            step={5}
            marks
            valueLabelDisplay="auto"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog('')}>{t('common.cancel')}</Button>
          <Button onClick={handleBikeSubmit} variant="contained">
            {t('bike.sendToBike')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sleep Dialog */}
      <Dialog open={openDialog === 'sleep'} onClose={() => setOpenDialog('')}>
        <DialogTitle>{t('sleep.dialogTitle')}</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            {t('sleep.dialogText')}
          </Typography>
          <Slider
            value={sleepMinutes}
            onChange={(e, v) => setSleepMinutes(v as number)}
            min={15}
            max={480}
            step={15}
            marks
            valueLabelDisplay="auto"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog('')}>{t('common.cancel')}</Button>
          <Button onClick={handleSleepSubmit} variant="contained">
            {t('sleep.sendToSleep')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ban URL Dialog */}
      <Dialog open={openDialog === 'ban'} onClose={() => setOpenDialog('')}>
        <DialogTitle>{t('browser.banDialogTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {selectedDevice?.current_url}
          </Typography>
          <List>
            <ListItemButton
              onClick={async () => {
                await handleBanCurrentUrl(selectedDevice.id, 'page');
                setOpenDialog('');
              }}
            >
              <ListItemIcon>
                <LinkIcon color="warning" />
              </ListItemIcon>
              <ListItemText
                primary={t('browser.banPage')}
                secondary={selectedDevice?.current_url}
                secondaryTypographyProps={{ noWrap: true, sx: { maxWidth: 300 } }}
              />
            </ListItemButton>
            <ListItemButton
              onClick={async () => {
                await handleBanCurrentUrl(selectedDevice.id, 'domain');
                setOpenDialog('');
              }}
            >
              <ListItemIcon>
                <Language color="error" />
              </ListItemIcon>
              <ListItemText
                primary={`${t('browser.banDomain')}: ${selectedDevice?.current_url ? getDomainFromUrl(selectedDevice.current_url) : ''}`}
              />
            </ListItemButton>
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog('')}>{t('common.cancel')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ControlsPage;
